import { z } from 'zod';
const id = z.string().min(1).max(120);
const actor = z.enum(['jordan', 'marcus', 'elena', 'noor', 'amara']);
const text = z.string().trim().min(1).max(12000);
const title = z.string().trim().min(1).max(200);
const references = z.array(id).max(30);
export const fieldOutcomeIdSchema = z.enum(['knowledge', 'trade', 'recovery']);
export const fieldDecisionStatusSchema = z.enum(['open', 'proposed', 'confirmed', 'deferred']);
export const fieldTicketPrioritySchema = z.enum(['high', 'normal', 'low']);
const decisionFields = {
    title,
    kind: z.enum(['requirement', 'assumption', 'question', 'scope']),
    description: text,
    owner: actor,
    impact: z.enum(['low', 'medium', 'high']),
    sourceIds: references,
    acceptance: z.array(z.string().trim().min(1).max(1000)).max(20),
    outcomeIds: z.array(fieldOutcomeIdSchema).max(3),
};
export const fieldDecisionSchema = z.object({
    id,
    ...decisionFields,
    version: z.number().int().positive(),
    sourceRevisions: z.record(z.string(), z.string()),
    status: fieldDecisionStatusSchema,
    /** Exact confirmation conversation for this business version; absent on unsent edits. */
    confirmationOperationId: id.optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    history: z.array(z.object({
        version: z.number().int().positive(),
        ...decisionFields,
        sourceRevisions: z.record(z.string(), z.string()),
        status: fieldDecisionStatusSchema,
        by: z.union([actor, z.literal('learner')]),
        rationale: z.string(),
        at: z.string(),
    })),
    resolution: z
        .object({
        actorId: actor,
        rationale: text,
        version: z.number().int().positive(),
        at: z.string(),
        /** Server-recorded turn that made this disposition, when available. */
        operationId: id.optional(),
    })
        .optional(),
});
export const fieldFindingCategorySchema = z.enum([
    'defect',
    'missed_discovery',
    'clarification',
    'scope_change',
    'improvement',
    'prevention',
]);
export const fieldFindingSchema = z.object({
    id,
    title,
    description: text,
    category: fieldFindingCategorySchema,
    owner: actor,
    decisionIds: references,
    decisionRefs: z.array(z.object({ decisionId: id, version: z.number().int().positive() })),
    evidenceIds: references,
    prId: id.optional(),
    revision: id.optional(),
    status: z.enum(['open', 'ticketed', 'resolved', 'deferred', 'disputed']),
    ticketId: id.optional(),
    response: z.string().optional(),
    resolution: z
        .object({ actorId: actor, rationale: text, evidenceIds: references, at: z.string() })
        .optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const fieldDiscoverySchema = z.object({
    version: z.literal('1'),
    brief: z.object({
        id,
        version: z.number().int().positive(),
        title,
        summary: text,
        body: text,
        successMeasures: z.array(z.string()),
        constraints: z.array(z.string()),
        startingPoints: z.array(z.string()),
        owner: actor,
    }),
    outcomes: z.array(z.object({
        id: fieldOutcomeIdSchema,
        title,
        description: text,
        acceptance: z.array(z.string()),
    })),
    decisions: z.array(fieldDecisionSchema),
    findings: z.array(fieldFindingSchema),
});
const ticketFields = {
    title,
    description: text,
    owner: actor,
    priority: fieldTicketPrioritySchema,
    acceptance: z.array(z.string().trim().min(1).max(2000)).min(1).max(20),
    dependencies: references,
    decisionIds: references,
    outcomeIds: z.array(fieldOutcomeIdSchema).max(3),
};
export const fieldDecisionRecordActionSchema = z.object({
    type: z.literal('decision.record'),
    decisionId: id.optional(),
    expectedVersion: z.number().int().positive().optional(),
    ...decisionFields,
});
export const fieldDecisionSubmitActionSchema = z.object({
    type: z.literal('decision.submit'),
    decisionId: id,
    expectedVersion: z.number().int().positive(),
});
export const fieldDecisionSubmitBatchActionSchema = z.object({
    type: z.literal('decision.submit_batch'),
    decisions: z
        .array(z.object({ decisionId: id, expectedVersion: z.number().int().positive() }))
        .min(1)
        .max(5)
        .refine((items) => new Set(items.map((item) => item.decisionId)).size === items.length, 'Choose each decision once'),
});
export const fieldTicketCreateActionSchema = z.object({
    type: z.literal('ticket.create'),
    ...ticketFields,
    findingId: id.optional(),
});
export const fieldTicketRefineActionSchema = z.object({
    type: z.literal('ticket.refine'),
    ticketId: id,
    expectedVersion: z.number().int().positive(),
    ...ticketFields,
});
export const fieldFindingRespondActionSchema = z.object({
    type: z.literal('finding.respond'),
    findingId: id,
    action: z.enum(['dispute', 'request_resolution', 'request_deferral']),
    response: text,
    sourceIds: references,
});
export const fieldDiscoveryActionSchemas = [
    z.object({ type: z.literal('discovery.start') }),
    fieldDecisionRecordActionSchema,
    fieldDecisionSubmitActionSchema,
    fieldDecisionSubmitBatchActionSchema,
    fieldTicketCreateActionSchema,
    fieldTicketRefineActionSchema,
    fieldFindingRespondActionSchema,
];
